import { useAtomValue } from 'jotai';

import type { LeadsTableRow,LeadStageConfig, LeadStageType } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useGetPipelines } from '../api/useGetPipelines';
import { useUpdateLeadStage } from '../api/useUpdateLeadStage';
import { leadsParamsAtom } from '../atoms/leadsAtoms';

interface UseStageSelectionParams {
	item: LeadsTableRow;
	stageReason: string;
}

interface UseStageSelectionResult {
	stageOptions: LeadStageConfig[];
	currentStageType: LeadStageType | undefined;
	handleStageSelect: (stageId: string) => void;
}

/**
 * Shared hook for stage selection logic.
 * Encapsulates pipeline lookup, stage options, and mutation handler
 * for use in both StageActionMenu (table) and StageDropdown (drawer).
 */
export const useStageSelection = ({
	item,
	stageReason,
}: UseStageSelectionParams): UseStageSelectionResult => {
	const servicesProviderId = useCurrentServicesProviderId();
	const { data: pipelines } = useGetPipelines();
	const { mutate: updateLeadStage } = useUpdateLeadStage();
	const leadsParams = useAtomValue(leadsParamsAtom);

	const pipelineConfig = pipelines
		? Object.values(pipelines).find((p) => p.type === item.pipeline.type)
		: undefined;

	const stageOptions = pipelineConfig?.stages ?? [];
	const currentStageType = stageOptions.find((s) => s.stage === item.stage.type)?.stage;

	const handleStageSelect = (stageId: string): void => {
		if (!servicesProviderId || !item.id || stageId === currentStageType) return;

		const targetStageConfig = stageOptions.find((s) => s.stage === stageId);
		const targetStageName = targetStageConfig?.name || '';

		updateLeadStage({
			leadId: item.id,
			servicesProviderId,
			stage: stageId as LeadStageType,
			stageReason,
			optimisticUpdateContext: {
				sourceStage: item.stage.type,
				targetStageName,
				currentQueryParams: leadsParams,
			},
		});
	};

	return { stageOptions, currentStageType, handleStageSelect };
};
